import React, { useMemo, useState } from "react";
import { toast } from "sonner";
import { MessageCircle, Loader2, CalendarCheck, User, Ruler, HeartPulse, Salad, Package, CalendarDays, ShieldCheck, Check } from "lucide-react";
import { api, openWhatsApp } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Field, Select, TextArea, Section, SectionTitle, Chips } from "@/components/BookingFields";

const GENDERS = ["Female", "Male", "Other"];
const DIETS = ["Vegetarian", "Eggetarian", "Non-Vegetarian", "Other"];
const ACTIVITY = ["Sedentary", "Light", "Moderate", "Active"];
const DIGESTIVE = ["Gas", "Acidity", "Constipation", "Loose Motion", "Bloating", "IBS symptoms", "None"];
const CONTACT_TIMES = ["Morning (9–12)", "Afternoon (12–4)", "Evening (4–8)", "Anytime"];

const INITIAL = {
  full_name: "", age: "", gender: "", contact: "", whatsapp: "", email: "", city: "", occupation: "",
  height_cm: "", weight_kg: "", target_weight_kg: "", waist_cm: "", hip_cm: "", chest_cm: "", neck_cm: "", arm_cm: "", thigh_cm: "",
  concern: "", medical_conditions: "", medications: "", allergies: "", medical_history: "", reports: "", health_extra: "",
  diet_preference: "", food_preferences: "", activity_level: "", exercise_routine: "", sleep_hours: "", water_intake: "", meal_timing: "", digestive: [], lifestyle_extra: "",
  package_id: "", start_date: "", slot: "", message: "", confirm: false,
};

const PHONE_RE = /^(\+?91[\s-]?)?[6-9]\d{9}$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function Booking() {
  const { settings } = useApp();
  const [form, setForm] = useState(INITIAL);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const packages = settings?.packages || [];
  const selected = packages.find((p) => p.id === form.package_id);

  const update = (k) => (e) => {
    const v = e?.target ? (e.target.type === "checkbox" ? e.target.checked : e.target.value) : e;
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((er) => ({ ...er, [k]: undefined }));
  };

  const bmi = useMemo(() => {
    const h = parseFloat(form.height_cm) / 100;
    const w = parseFloat(form.weight_kg);
    if (!h || !w || h < 0.5 || h > 2.6) return null;
    return (w / (h * h)).toFixed(1);
  }, [form.height_cm, form.weight_kg]);

  const validate = () => {
    const er = {};
    if (!form.full_name.trim()) er.full_name = "Full name is required";
    const age = Number(form.age);
    if (!form.age || age < 1 || age > 120) er.age = "Enter a valid age (1–120)";
    if (!form.gender) er.gender = "Select gender";
    if (!PHONE_RE.test(form.contact.replace(/\s/g, ""))) er.contact = "Enter a valid 10-digit Indian mobile number";
    if (!PHONE_RE.test(form.whatsapp.replace(/\s/g, ""))) er.whatsapp = "Enter a valid WhatsApp number";
    if (form.email && !EMAIL_RE.test(form.email)) er.email = "Enter a valid email address";
    if (!form.city.trim()) er.city = "City is required";
    const h = Number(form.height_cm), w = Number(form.weight_kg), tw = Number(form.target_weight_kg);
    if (!form.height_cm || h < 50 || h > 260) er.height_cm = "Height should be 50–260 cm";
    if (!form.weight_kg || w < 10 || w > 400) er.weight_kg = "Weight should be 10–400 kg";
    if (form.target_weight_kg && (tw < 10 || tw > 400)) er.target_weight_kg = "Target weight should be 10–400 kg";
    if (!form.concern.trim()) er.concern = "Main health concern is required";
    if (!form.diet_preference) er.diet_preference = "Select dietary preference";
    if (!form.activity_level) er.activity_level = "Select activity level";
    if (!form.package_id) er.package_id = "Please select a consultation package";
    if (!form.start_date) er.start_date = "Select a preferred start date";
    if (!form.slot) er.slot = "Select preferred contact time";
    if (!form.confirm) er.confirm = "Please confirm the information is accurate";
    setErrors(er);
    return er;
  };

  const submit = async (e) => {
    e.preventDefault();
    const er = validate();
    const firstKey = Object.keys(er)[0];
    if (firstKey) {
      toast.error(er[firstKey]);
      document.querySelector(`[data-field="${firstKey}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setLoading(true);
    const payload = {
      ...form,
      digestive: form.digestive.join(", "),
      bmi: bmi || "",
      package_name: selected?.name,
      package_price: selected?.price,
      confirm: undefined,
    };
    try {
      await api.post("/leads", payload);
    } catch {
      // proceed to WhatsApp even if save fails
    }
    const msg =
      `*New Consultation Booking* 🌿\n\n` +
      `👤 *Name:* ${form.full_name}\n` +
      `🎂 *Age:* ${form.age}   *Gender:* ${form.gender}\n` +
      `📞 *Mobile:* ${form.contact}\n` +
      `💬 *WhatsApp:* ${form.whatsapp}\n\n` +
      `📦 *Package:* ${selected.name}\n` +
      `💰 *Price:* ₹${selected.price.toLocaleString("en-IN")}\n\n` +
      `📏 *Height:* ${form.height_cm} cm\n` +
      `⚖️ *Current Weight:* ${form.weight_kg} kg\n` +
      `🎯 *Target Weight:* ${form.target_weight_kg || "-"} kg\n` +
      `📊 *BMI:* ${bmi || "-"}\n\n` +
      `🩺 *Main Concern:* ${form.concern}\n` +
      `📋 *Medical Conditions:* ${form.medical_conditions || "None"}\n` +
      `🥗 *Diet:* ${form.diet_preference}\n\n` +
      `📅 *Preferred Start:* ${form.start_date}\n` +
      `⏰ *Contact Time:* ${form.slot}\n` +
      `📝 *Message:* ${form.message || "-"}\n\n` +
      `Please share the payment details to confirm my booking. Thank you!`;
    setLoading(false);
    toast.success("Opening WhatsApp…");
    openWhatsApp(msg, settings?.whatsapp_number);
  };

  return (
    <div className="animate-fade-in">
      <div className="px-5 pt-4 pb-3">
        <div className="flex items-center gap-2 text-emerald-600 dark:text-emerald-400">
          <CalendarCheck className="w-4 h-4" />
          <span className="text-[11px] font-semibold tracking-wider uppercase">Book a Consultant</span>
        </div>
        <h1 className="text-2xl font-extrabold text-emerald-950 dark:text-emerald-50 font-head">Customized Consultation</h1>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Fill in your details — our nutritionist will design a plan for you.</p>
      </div>

      <form onSubmit={submit} noValidate className="px-4 space-y-4 pb-8" data-testid="booking-form">
        <Section>
          <SectionTitle icon={User} step={1} title="Personal Information" />
          <Field label="Full Name" name="full_name" testid="booking-name" value={form.full_name} onChange={update("full_name")} placeholder="Your full name" required error={errors.full_name} />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Age" name="age" testid="booking-age" type="number" inputMode="numeric" min="1" max="120" value={form.age} onChange={update("age")} placeholder="e.g. 29" required error={errors.age} />
            <Select label="Gender" name="gender" testid="booking-gender" value={form.gender} onChange={update("gender")} options={GENDERS} placeholder="Select" required error={errors.gender} />
          </div>
          <Field label="Mobile Number" name="contact" testid="booking-contact" type="tel" inputMode="tel" value={form.contact} onChange={update("contact")} placeholder="10-digit mobile number" required error={errors.contact} />
          <Field label="WhatsApp Number" name="whatsapp" testid="booking-whatsapp" type="tel" inputMode="tel" value={form.whatsapp} onChange={update("whatsapp")} placeholder="Number for WhatsApp follow-up" required error={errors.whatsapp} hint={form.contact && !form.whatsapp ? "Same as mobile?" : undefined} onHint={() => update("whatsapp")(form.contact)} />
          <Field label="Email Address" name="email" testid="booking-email" type="email" inputMode="email" value={form.email} onChange={update("email")} placeholder="you@example.com" error={errors.email} />
          <div className="grid grid-cols-2 gap-3">
            <Field label="City" name="city" testid="booking-city" value={form.city} onChange={update("city")} placeholder="Your city" required error={errors.city} />
            <Field label="Occupation" name="occupation" testid="booking-occupation" value={form.occupation} onChange={update("occupation")} placeholder="e.g. Teacher" />
          </div>
        </Section>

        <Section>
          <SectionTitle icon={Ruler} step={2} title="Body & Measurements" />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Height (cm)" name="height_cm" testid="booking-height" type="number" inputMode="decimal" value={form.height_cm} onChange={update("height_cm")} placeholder="e.g. 162" required error={errors.height_cm} />
            <Field label="Current Weight (kg)" name="weight_kg" testid="booking-weight" type="number" inputMode="decimal" value={form.weight_kg} onChange={update("weight_kg")} placeholder="e.g. 68" required error={errors.weight_kg} />
            <Field label="Target Weight (kg)" name="target_weight_kg" testid="booking-target-weight" type="number" inputMode="decimal" value={form.target_weight_kg} onChange={update("target_weight_kg")} placeholder="e.g. 60" error={errors.target_weight_kg} />
            <Field label="Waist (cm)" testid="booking-waist" type="number" inputMode="decimal" value={form.waist_cm} onChange={update("waist_cm")} placeholder="e.g. 84" />
            <Field label="Hip (cm)" testid="booking-hip" type="number" inputMode="decimal" value={form.hip_cm} onChange={update("hip_cm")} placeholder="e.g. 98" />
            <Field label="Chest (cm)" testid="booking-chest" type="number" inputMode="decimal" value={form.chest_cm} onChange={update("chest_cm")} placeholder="e.g. 92" />
            <Field label="Neck (cm)" testid="booking-neck" type="number" inputMode="decimal" value={form.neck_cm} onChange={update("neck_cm")} placeholder="e.g. 34" />
            <Field label="Mid-Upper Arm (cm)" testid="booking-arm" type="number" inputMode="decimal" value={form.arm_cm} onChange={update("arm_cm")} placeholder="e.g. 28" />
            <Field label="Thigh (cm)" testid="booking-thigh" type="number" inputMode="decimal" value={form.thigh_cm} onChange={update("thigh_cm")} placeholder="e.g. 55" />
          </div>
          <div data-testid="booking-bmi" className="flex items-center justify-between p-3.5 rounded-2xl bg-emerald-50 dark:bg-emerald-800/50 border border-emerald-100 dark:border-emerald-700">
            <div>
              <p className="text-xs font-semibold text-emerald-800 dark:text-emerald-200">Your BMI (informational)</p>
              <p className="text-[11px] text-emerald-700/70 dark:text-emerald-300/60">Calculated from height & current weight</p>
            </div>
            <span data-testid="booking-bmi-value" className="text-2xl font-extrabold text-emerald-800 dark:text-emerald-100 font-head">{bmi ?? "—"}</span>
          </div>
        </Section>

        <Section>
          <SectionTitle icon={HeartPulse} step={3} title="Health Information" />
          <Field label="Main Health Concern" name="concern" testid="booking-concern" value={form.concern} onChange={update("concern")} placeholder="e.g. Weight loss, PCOS, thyroid, diabetes…" required error={errors.concern} />
          <TextArea label="Existing Medical Conditions" testid="booking-conditions" value={form.medical_conditions} onChange={update("medical_conditions")} placeholder="e.g. Hypothyroid, high BP — or write 'None'" />
          <TextArea label="Current Medications" testid="booking-medications" value={form.medications} onChange={update("medications")} placeholder="Names & doses if known" />
          <TextArea label="Food Allergies / Intolerances" testid="booking-allergies" value={form.allergies} onChange={update("allergies")} placeholder="e.g. Lactose, peanuts, gluten" />
          <TextArea label="Previous Medical or Diet History" testid="booking-history" value={form.medical_history} onChange={update("medical_history")} placeholder="Earlier diets tried, surgeries, pregnancies, etc." />
          <TextArea label="Recent Blood Test / Health Report Information" testid="booking-reports" value={form.reports} onChange={update("reports")} placeholder="e.g. HbA1c 6.2, TSH 5.1, Vitamin D low" />
          <TextArea label="Additional Health Information" testid="booking-health-extra" value={form.health_extra} onChange={update("health_extra")} placeholder="Anything else our nutritionist should know" />
        </Section>

        <Section>
          <SectionTitle icon={Salad} step={4} title="Food & Lifestyle" />
          <Select label="Dietary Preference" name="diet_preference" testid="booking-diet" value={form.diet_preference} onChange={update("diet_preference")} options={DIETS} placeholder="Select" required error={errors.diet_preference} />
          <TextArea label="Food Preferences / Foods Avoided" testid="booking-food-prefs" value={form.food_preferences} onChange={update("food_preferences")} placeholder="Likes, dislikes, foods you avoid" />
          <Select label="Daily Activity Level" name="activity_level" testid="booking-activity" value={form.activity_level} onChange={update("activity_level")} options={ACTIVITY} placeholder="Select" required error={errors.activity_level} />
          <Field label="Exercise Routine" testid="booking-exercise" value={form.exercise_routine} onChange={update("exercise_routine")} placeholder="e.g. 30 min walk, 4 days/week" />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Average Sleep (hours)" testid="booking-sleep" type="number" inputMode="decimal" value={form.sleep_hours} onChange={update("sleep_hours")} placeholder="e.g. 7" />
            <Field label="Water Intake (litres/day)" testid="booking-water" type="number" inputMode="decimal" value={form.water_intake} onChange={update("water_intake")} placeholder="e.g. 2.5" />
          </div>
          <Field label="Typical Meal Timing" testid="booking-meal-timing" value={form.meal_timing} onChange={update("meal_timing")} placeholder="e.g. Breakfast 9am, Lunch 2pm, Dinner 9pm" />
          <Chips label="Any Digestive Problems" testid="booking-digestive" options={DIGESTIVE} value={form.digestive} onChange={(v) => update("digestive")(v)} />
          <TextArea label="Additional Lifestyle Information" testid="booking-lifestyle-extra" value={form.lifestyle_extra} onChange={update("lifestyle_extra")} placeholder="Work hours, travel, stress, smoking/alcohol, etc." />
        </Section>

        <Section>
          <SectionTitle icon={Package} step={5} title="Consultation Package" required />
          <div className="space-y-2.5" data-field="package_id">
            {packages.map((p) => {
              const active = form.package_id === p.id;
              return (
                <button
                  type="button"
                  key={p.id}
                  data-testid={`booking-package-${p.id}`}
                  onClick={() => update("package_id")(p.id)}
                  className={`w-full text-left p-4 rounded-2xl border-2 transition-all ${
                    active
                      ? "border-emerald-600 bg-emerald-50 dark:bg-emerald-800/60 shadow-md shadow-emerald-600/10"
                      : "border-slate-200 dark:border-emerald-800 bg-white dark:bg-emerald-900/40 hover:border-emerald-300"
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <p className="text-sm font-bold text-emerald-950 dark:text-emerald-50 leading-snug">{p.name}</p>
                      <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1 leading-snug">{p.description}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-lg font-extrabold text-emerald-700 dark:text-emerald-300 font-head">₹{p.price.toLocaleString("en-IN")}</p>
                      <p className="text-[10px] text-slate-400">{p.duration}</p>
                    </div>
                  </div>
                  <div className={`mt-3 flex items-center gap-1.5 text-[11px] font-bold ${active ? "text-emerald-700 dark:text-emerald-300" : "text-slate-400"}`}>
                    <span className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${active ? "bg-emerald-600 border-emerald-600" : "border-slate-300"}`}>
                      {active && <Check className="w-3 h-3 text-white" />}
                    </span>
                    {active ? "Selected" : "Tap to select"}
                  </div>
                </button>
              );
            })}
            {packages.length === 0 && <p className="text-sm text-slate-400 py-3 text-center">Loading packages…</p>}
          </div>
          {errors.package_id && <p className="text-[11px] text-rose-600 ml-1" data-testid="booking-package-error">{errors.package_id}</p>}
        </Section>

        <Section>
          <SectionTitle icon={CalendarDays} step={6} title="Booking Details" />
          <Field label="Preferred Start Date" name="start_date" testid="booking-start-date" type="date" min={new Date().toISOString().slice(0, 10)} value={form.start_date} onChange={update("start_date")} required error={errors.start_date} />
          <Select label="Preferred Contact Time" name="slot" testid="booking-slot" value={form.slot} onChange={update("slot")} options={CONTACT_TIMES} placeholder="Select" required error={errors.slot} />
          <TextArea label="Additional Message" testid="booking-message" value={form.message} onChange={update("message")} placeholder="Any questions or special requests" />
          <label data-field="confirm" className={`flex items-start gap-3 p-3.5 rounded-2xl border cursor-pointer select-none ${errors.confirm ? "border-rose-300 bg-rose-50/50" : "border-slate-200 dark:border-emerald-800 bg-white dark:bg-emerald-900/40"}`}>
            <input type="checkbox" data-testid="booking-confirm" checked={form.confirm} onChange={update("confirm")} className="mt-0.5 w-4 h-4 rounded accent-emerald-600" />
            <span className="text-xs text-slate-700 dark:text-slate-300 leading-relaxed">I confirm that the information provided by me is accurate to the best of my knowledge. <span className="text-rose-500">*</span></span>
          </label>
        </Section>

        <Section>
          <SectionTitle icon={ShieldCheck} step={7} title="WhatsApp Payment" />
          {selected && (
            <div className="flex items-center justify-between text-sm px-1" data-testid="booking-summary">
              <span className="text-slate-600 dark:text-slate-300">{selected.name}</span>
              <span className="font-extrabold text-emerald-700 dark:text-emerald-300">₹{selected.price.toLocaleString("en-IN")}</span>
            </div>
          )}
          <button
            type="submit"
            data-testid="intake-submit-whatsapp-btn"
            disabled={loading}
            className="w-full flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1ebe5b] text-white font-bold py-4 rounded-2xl transition-colors shadow-lg shadow-emerald-600/25 disabled:opacity-60"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <MessageCircle className="w-5 h-5" />}
            Continue to WhatsApp for Payment
          </button>
          <p className="text-center text-[11px] text-slate-400 leading-relaxed">No online payment gateway — payment is confirmed manually over WhatsApp. Your details are shared securely with our nutritionist only.</p>
          <p className="text-center text-[11px] text-slate-500 dark:text-slate-400 leading-relaxed border-t border-slate-100 dark:border-emerald-800 pt-3">
            This consultation is for personalized nutrition guidance. Medical diagnosis and medication changes are not provided through this form.
          </p>
        </Section>
      </form>
    </div>
  );
}
